import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import type { Endpoint, HttpMethod, SearchMatch } from '../types';
import { searchIndex } from '../search';
import { parseQuery, getTokenDisplayInfo, type Token, type TokenType } from '../queryParser';

interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  onResults: (results: { endpoint: Endpoint; matches: SearchMatch[] }[]) => void;
  filters: {
    methods: HttpMethod[];
    specs: string[];
    tags: string[];
  };
  onFiltersChange: (filters: { methods?: HttpMethod[]; specs?: string[]; tags?: string[] }) => void;
}

export function SearchBar({ value, onChange, onResults, filters, onFiltersChange }: SearchBarProps) {
  const [showFilters, setShowFilters] = useState(false);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [allSpecs, setAllSpecs] = useState<{ id: string; title: string }[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const methods: HttpMethod[] = ['get', 'post', 'put', 'delete', 'patch', 'head', 'options', 'trace'];

  useEffect(() => {
    setAllTags(searchIndex.getAllTags());
    setAllSpecs(searchIndex.getAllSpecs());
  }, []);

  // Parse query for visual indicators
  const parsedQuery = useMemo(() => parseQuery(value), [value]);

  // Proper debounced search with 250ms delay
  useEffect(() => {
    setIsSearching(true);
    
    const debounceTimer = setTimeout(() => {
      const results = searchIndex.searchWithFilters(value, {
        methods: filters.methods,
        specs: filters.specs,
        tags: filters.tags,
      });
      onResults(results);
      setIsSearching(false);
    }, 250); // 250ms debounce for better performance

    return () => clearTimeout(debounceTimer);
  }, [value, filters, onResults]);

  // Close filters when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowFilters(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === '/' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const target = e.target as HTMLElement;
        if (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA') {
          e.preventDefault();
          inputRef.current?.focus();
        }
      }
      if (e.key === 'Escape') {
        setShowFilters(false);
        if (document.activeElement === inputRef.current) {
          inputRef.current?.blur();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const toggleMethod = (method: HttpMethod) => {
    const newMethods = filters.methods.includes(method)
      ? filters.methods.filter((m) => m !== method)
      : [...filters.methods, method];
    onFiltersChange({ methods: newMethods });
  };

  const toggleSpec = (specId: string) => {
    const newSpecs = filters.specs.includes(specId)
      ? filters.specs.filter((s) => s !== specId)
      : [...filters.specs, specId];
    onFiltersChange({ specs: newSpecs });
  };

  const toggleTag = (tag: string) => {
    const newTags = filters.tags.includes(tag)
      ? filters.tags.filter((t) => t !== tag)
      : [...filters.tags, tag];
    onFiltersChange({ tags: newTags });
  };

  const hasFilters = filters.methods.length > 0 || filters.specs.length > 0 || filters.tags.length > 0;
  const hasComplexQuery = parsedQuery.hasComplexQuery && parsedQuery.tokens.length > 0;

  // Render token with visual indicator
  const renderToken = (token: Token, index: number) => {
    const displayInfo = getTokenDisplayInfo(token.type);
    const isOperator = token.type === 'and' || token.type === 'or';
    
    return (
      <span
        key={index}
        className={`query-token query-token-${token.type}`}
        style={{
          backgroundColor: displayInfo.bgColor,
          color: displayInfo.color,
          borderColor: displayInfo.color,
        }}
        title={displayInfo.description}
      >
        {token.type === 'in' && (
          <>
            <span className="query-token-icon">↓</span>
            <span className="query-token-label">in:</span>
          </>
        )}
        {token.type === 'out' && (
          <>
            <span className="query-token-icon">↑</span>
            <span className="query-token-label">out:</span>
          </>
        )}
        {token.type === 'exact' && (
          <>
            <span className="query-token-icon">"</span>
            <span className="query-token-label">exact:</span>
          </>
        )}
        {isOperator && (
          <span className="query-token-operator">{token.raw}</span>
        )}
        {(token.type === 'term' || token.type === 'in' || token.type === 'out' || token.type === 'exact') && (
          <span className="query-token-value">{token.value}</span>
        )}
        {(token.type === 'lparen' || token.type === 'rparen') && (
          <span className="query-token-paren">{token.raw}</span>
        )}
      </span>
    );
  };

  return (
    <div className="search-container" ref={containerRef}>
      <div className="search-input-wrapper">
        <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          className="search-input"
          placeholder='(press / to focus) Search: ↓in: ↑out: "exact: AND OR'
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setShowFilters(false)}
        />
        {isSearching && (
          <div className="search-spinner" />
        )}
        {value && !isSearching && (
          <button className="search-clear" onClick={() => { onChange(''); inputRef.current?.focus(); }}>
            ×
          </button>
        )}
        <button
          className={`search-filter-btn ${hasFilters ? 'active' : ''}`}
          onClick={() => setShowFilters(!showFilters)}
          title="Toggle filters"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
          </svg>
          {hasFilters && <span className="filter-badge" />}
        </button>
      </div>



      {/* Query tokens visualization */}
      {hasComplexQuery && (
        <div className="query-tokens">
          <span className="query-tokens-label">Parsed:</span>
          <div className="query-tokens-list">
            {parsedQuery.tokens.map((token, index) => renderToken(token, index))}
          </div>
        </div>
      )}

      {/* Active filters display */}
      {hasFilters && (
        <div className="active-filters">
          {filters.methods.map((method) => (
            <button
              key={method}
              className="active-filter-chip method"
              onClick={() => toggleMethod(method)}
              title="Click to remove"
            >
              {method.toUpperCase()}
              <span className="remove-icon">×</span>
            </button>
          ))}
          {filters.tags.map((tag) => (
            <button
              key={tag}
              className="active-filter-chip"
              onClick={() => toggleTag(tag)}
              title="Click to remove"
            >
              {tag}
              <span className="remove-icon">×</span>
            </button>
          ))}
          {filters.specs.map((specId) => {
            const spec = allSpecs.find(s => s.id === specId);
            return (
              <button
                key={specId}
                className="active-filter-chip"
                onClick={() => toggleSpec(specId)}
                title="Click to remove"
              >
                {spec?.title || specId}
                <span className="remove-icon">×</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Filters dropdown */}
      {showFilters && (
        <div className="search-filters">
          <div className="filter-section">
            <h4>HTTP Methods</h4>
            <div className="filter-options">
              {methods.map((method) => (
                <button
                  key={method}
                  className={`filter-chip ${filters.methods.includes(method) ? 'active' : ''}`}
                  onClick={() => toggleMethod(method)}
                >
                  {method.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {allSpecs.length > 0 && (
            <div className="filter-section">
              <h4>API Specs</h4>
              <div className="filter-options">
                {allSpecs.map((spec) => (
                  <button
                    key={spec.id}
                    className={`filter-chip ${filters.specs.includes(spec.id) ? 'active' : ''}`}
                    onClick={() => toggleSpec(spec.id)}
                  >
                    {spec.title}
                  </button>
                ))}
              </div>
            </div>
          )}

          {allTags.length > 0 && (
            <div className="filter-section">
              <h4>Tags</h4>
              <div className="filter-options">
                {allTags.slice(0, 20).map((tag) => (
                  <button
                    key={tag}
                    className={`filter-chip ${filters.tags.includes(tag) ? 'active' : ''}`}
                    onClick={() => toggleTag(tag)}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          )}

          {hasFilters && (
            <button className="filter-clear" onClick={() => onFiltersChange({ methods: [], specs: [], tags: [] })}>
              Clear all filters
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Help tooltip component for query syntax
export function SearchHelpTooltip() {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="search-help">
      <button 
        className="search-help-btn"
        onClick={() => setIsOpen(!isOpen)}
        title="Search syntax help"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      </button>
      
      {isOpen && (
        <div className="search-help-tooltip">
          <h4>Search Syntax</h4>
          <div className="search-help-section">
            <code>in:keyword</code>
            <span>Search in input fields (parameters, request body)</span>
          </div>
          <div className="search-help-section">
            <code>out:keyword</code>
            <span>Search in output fields (responses)</span>
          </div>
          <div className="search-help-section">
            <code>exact:phrase</code>
            <span>Exact match only</span>
          </div>
          <div className="search-help-section">
            <code>AND</code>
            <span>All terms must match (default for space-separated terms)</span>
          </div>
          <div className="search-help-section">
            <code>OR</code>
            <span>Any term can match</span>
          </div>
          <div className="search-help-example">
            <strong>Example:</strong> <code>in:user AND out:json</code>
          </div>
        </div>
      )}
    </div>
  );
}
